<template>
  <AppDrawer
    v-model="open"
    :title="activity?.name || t('promo.acts.detailTitle')"
    title-align="left"
    padded
    body-padding="0 0 96px"
    @back="emit('close')"
  >
    <template v-if="activity">
      <div class="detail-banner" :class="{ 'has-overlay': showOverlay }">
        <EncryptedImg
          v-if="banner.url"
          class="banner-img"
          :url="banner.url"
          :key-hex="banner.key"
          eager
          fit="cover"
        />
        <div v-else class="banner-img banner-placeholder" />
        <div v-if="showOverlay" class="ah-body">
          <span v-if="activity.categoryName" class="ah-tag">{{ activity.categoryName }}</span>
          <div v-if="activity.name" class="ah-title">{{ activity.name }}</div>
          <div v-if="info.rewardHighlight" class="ah-reward">{{ info.rewardHighlight }}</div>
        </div>
        <span class="ah-state" :class="[`state-${activity.stateClass}`]">{{
          activity.statusLabel
        }}</span>
      </div>

      <template v-if="showOverlay">
        <section v-if="info.rows.length" class="detail-section">
          <h3>{{ t('promo.acts.detail.secDetail') }}</h3>
          <div class="list">
            <div v-for="row in info.rows" :key="row.label" class="row">
              <div class="rt">{{ row.label }}</div>
              <div class="rv">{{ row.value }}</div>
            </div>
          </div>
        </section>
      </template>

      <section v-if="info.rules.length" class="detail-section">
        <h3>{{ t('promo.acts.detail.secRules') }}</h3>
        <ul class="rule-list">
          <li v-for="(rule, i) in info.rules" :key="i">{{ rule }}</li>
        </ul>
      </section>

      <div class="detail-footer">
        <button
          v-if="activity.actionLabel"
          type="button"
          class="cta-btn"
          :disabled="!activity.actionEnabled"
          @click="emit('action', activity)"
        >
          {{ activity.actionLabel }}
        </button>
      </div>
    </template>
  </AppDrawer>
</template>

<script setup lang="ts">
import AppDrawer from '~/components/AppDrawer.vue'
import EncryptedImg from '~/components/EncryptedImg.vue'
import { usePromoDetailInfo } from '../composables/usePromoDetailInfo'
import type { PromoActivity } from '../types'

const props = defineProps<{
  activity: PromoActivity | null
  loading?: boolean
}>()

const emit = defineEmits<{
  close: []
  action: [activity: PromoActivity]
}>()

const open = defineModel<boolean>({ default: false })

const { t } = useI18n()

/** display 模板纯图展示（文案烤在图里）；其余模板背景图 + 标题叠加 + 结构化行 */
const showOverlay = computed(() => !!props.activity && props.activity.templateCode !== 'display')

/** 详情图优先，缺详情图回退封面，url/key 要成对取；都没有则占位 */
const banner = computed(() => {
  const a = props.activity
  if (!a) return { url: '', key: '' }
  return a.detailUrl ? { url: a.detailUrl, key: a.detailKey } : { url: a.coverUrl, key: a.coverKey }
})

/** 无详情图时按模板拼接结构化详情（交接文档口径） */
const { info } = usePromoDetailInfo(toRef(props, 'activity'))
</script>

<style scoped lang="scss">
.detail-banner {
  position: relative;
  min-height: 150px;
  overflow: hidden;
  border-bottom: 1px solid var(--hairline);

  &.has-overlay {
    position: relative;
    height: 150px;
    overflow: hidden;

    &::after {
      content: '';
      position: absolute;
      inset: 0;
      background: linear-gradient(
        180deg,
        rgba(10, 10, 20, 0.15),
        rgba(10, 10, 20, 0.55) 55%,
        var(--bg)
      );
    }

    .banner-img {
      position: absolute;
      inset: 0;
      height: 100%;
    }
  }
}

.banner-img {
  display: block;
  width: 100%;
  min-height: 150px;
  object-fit: cover;
}

.banner-placeholder {
  background:
    radial-gradient(120% 140% at 85% 20%, rgba(255, 200, 90, 0.18), transparent 55%),
    linear-gradient(100deg, #3a1a24, #1c0a12 62%);
}

.ah-body {
  position: absolute;
  left: 16px;
  right: 16px;
  bottom: 12px;
  z-index: 2;
}

.ah-tag {
  display: inline-block;
  font-size: 11px;
  font-weight: 700;
  color: var(--gold-hi);
  background: rgba(0, 0, 0, 0.35);
  padding: 2px 9px;
  border-radius: var(--r-pill);
}

.ah-title {
  font-size: 22px;
  font-weight: 900;
  color: #fff;
  margin-top: 7px;
  letter-spacing: 0.5px;
  text-shadow: 0 2px 6px rgba(0, 0, 0, 0.5);
}

.ah-reward {
  font-size: 13px;
  font-weight: 800;
  color: var(--gold-hi);
  margin-top: 3px;
}

.ah-state {
  position: absolute;
  right: 16px;
  bottom: 16px;
  font-size: 11px;
  z-index: 2;
  font-weight: 800;
  color: #fff;
  padding: 4px 11px;
  border-radius: var(--r-pill);

  &.state-claimable {
    background: var(--cta-claim);
  }

  &.state-claimed {
    background: rgba(120, 120, 140, 0.85);
  }

  &.state-progress {
    background: var(--cta-progress);
  }

  &.state-incomplete,
  &.state-unset {
    background: var(--cta-go);
  }

  &.state-waiting {
    background: var(--cta-wait-bg);
  }
}

.detail-section {
  padding: 0 14px;
  margin-top: 18px;

  h3 {
    margin: 0 2px 10px;
    padding-left: 9px;
    border-left: 3px solid var(--primary);
    font-size: 15px;
    font-weight: 800;
    color: var(--text);
  }
}

.list {
  overflow: hidden;
  background: var(--surface);
  border: 1px solid var(--hairline);
  border-radius: var(--r-lg);
}

.row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;

  &:not(:last-child) {
    border-bottom: 1px solid var(--hairline);
  }
}

.rt {
  flex: 1;
  font-size: 14px;
  font-weight: 700;
}

.rv {
  max-width: 60%;
  font-size: 14px;
  font-weight: 800;
  text-align: right;
}

.rule-list {
  margin: 0;
  padding: 0 2px;
  list-style: none;

  li {
    position: relative;
    padding: 0 0 9px 15px;
    font-size: 12.5px;
    line-height: 1.6;
    color: var(--muted);

    &::before {
      content: '';
      position: absolute;
      left: 2px;
      top: 8px;
      width: 5px;
      height: 5px;
      border-radius: 50%;
      background: var(--primary);
    }
  }
}

.detail-footer {
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 12px 16px calc(12px + env(safe-area-inset-bottom));
  background: linear-gradient(180deg, transparent, var(--bg) 22%);
  z-index: 5;
}

.cta-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-weight: 800;
  padding: 12px 16px;
  border-radius: var(--r-md);
  background: var(--cta-go);
  color: #fff;
  font-size: 15px;
  width: 100%;

  &:disabled {
    background: var(--cta-wait-bg);
    color: rgb(255 255 255 / 68%);
  }

  &:active:not(:disabled) {
    transform: scale(0.98);
  }
}
</style>
